import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useDispatch } from 'react-redux';
import { setIsConfirmationDialog } from '../store/slices/globalSlice';
import CustomButton from './CustomButton';

interface Props {
    handleClickConfirm: VoidFunction;
    // Second commit action, rendered only when the content carries a
    // `middleButtonText`. Both commits are outlined-vs-filled rather than one
    // button plus a checkbox: a tickbox can be clicked past in either state,
    // and we cannot then tell a deliberate choice from an unread default.
    handleClickMiddle?: VoidFunction;
    isLoading?: boolean;
}

export default function ConfirmationDialog({ handleClickConfirm, handleClickMiddle, isLoading }: Props) {
    const dispatch = useDispatch();
    const { confirmationDialogContent, isConfirmationDialog } = useSelector((state: RootState) => state.global);

    // Which commit is running, so the spinner sits on the button that was
    // actually pressed. With two commits both showing `isLoading`, a cancel
    // without email spun the "send email" button too — and the operator has no
    // way to tell which of the two the dialog is executing.
    const [pending, setPending] = useState<'confirm' | 'middle' | null>(null);

    // The component stays mounted while closed, so without this the next
    // opening inherits the previous run's spinner.
    useEffect(() => {
        if (!isConfirmationDialog) setPending(null);
    }, [isConfirmationDialog]);

    const handleClose = () => {
        dispatch(setIsConfirmationDialog(false));
    };

    const runConfirm = () => {
        setPending('confirm');
        handleClickConfirm();
    };

    const runMiddle = () => {
        setPending('middle');
        handleClickMiddle?.();
    };

    if (!isConfirmationDialog) return null;

    return (
        <div className={'fixed inset-0 z-[1000] flex items-center justify-center p-4'}>
            {/* Background overlay */}
            <div className={'fixed inset-0 bg-black bg-opacity-50 transition-opacity'} onClick={handleClose}></div>

            {/* Dialog container - centered */}
            <div
                className={'relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md mx-auto'}
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={'alert-dialog-title'}
                aria-describedby={'alert-dialog-description'}
            >
                {/* Dialog content */}
                <div className={'p-6'}>
                    <h3 className={'text-lg font-bold text-gray-900 mb-4'} id={'alert-dialog-title'}>
                        {confirmationDialogContent.headerText}
                    </h3>
                    <div className={'mt-2'}>
                        <p className={'text-sm text-foreground'} id={'alert-dialog-description'}>
                            {confirmationDialogContent.content}
                        </p>
                    </div>
                </div>

                {/* Dialog actions - using CustomButton.
                    flex-wrap so a third action wraps at ~390px instead of
                    squeezing all three onto one line. DOM order is reversed by
                    flex-row-reverse, so this renders left → right as:
                    abort · middle commit · primary commit. */}
                <div className={'px-6 py-4 flex flex-row-reverse flex-wrap gap-3'}>
                    <CustomButton
                        title={confirmationDialogContent.rightButtonText}
                        loading={isLoading && pending === 'confirm'}
                        disabled={isLoading}
                        onClick={runConfirm}
                    />
                    {confirmationDialogContent.middleButtonText && handleClickMiddle && (
                        <CustomButton
                            title={confirmationDialogContent.middleButtonText}
                            loading={isLoading && pending === 'middle'}
                            disabled={isLoading}
                            onClick={runMiddle}
                            isFilled={false}
                        />
                    )}
                    {/* `mr-auto` under flex-row-reverse pushes the abort to the
                        far edge, away from the commits. Without it the
                        keep-it action sits shoulder to shoulder with a
                        destructive one wearing the same outline — the choice
                        this dialog exists to make deliberate.

                        Never disabled: `isLoading` is the SHARED global flag,
                        which unrelated flows also set, so disabling the way out
                        can leave a dialog whose only escape is the backdrop. */}
                    <div className={'mr-auto'}>
                        <CustomButton
                            title={confirmationDialogContent.leftButtonText}
                            onClick={handleClose}
                            isFilled={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
